import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import Loader from "../../../ui/Loader";
import Modal from "../../../ui/Modal";
import Table from "../../../ui/Table";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageTestTypes = forwardRef((props, ref) => {
  const { user } = useContext(AuthContext);
  const [instituteData, setInstituteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form states
  const [newTestType, setNewTestType] = useState("");
  const [editTestType, setEditTestType] = useState({ index: null, value: "" });
  const [initialEditValue, setInitialEditValue] = useState("");
  const [isEditChanged, setIsEditChanged] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true),
  }));

  useEffect(() => {
    const fetchInstitute = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        setInstituteData(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        swalError("Failed to load institute data.");
      } finally {
        setLoading(false);
      }
    };
    fetchInstitute();
  }, [user.instituteId]);

  const testTypes = instituteData?.testTypes || [];

  const updateTestTypesOnServer = async (updatedTestTypes, successMsg) => {
    if (!instituteData) return;
    setIsUpdating(true);
    try {
      const form = new FormData();
      // Only append testTypes as JSON string
      form.append("testTypes", JSON.stringify(updatedTestTypes));
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${user.instituteId}`,
        {
          method: "PUT",
          body: form,
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update test types");
      }
      const data = await response.json();
      setInstituteData(data);
      swalSuccess(successMsg);
      setIsEditModalOpen(false);
      setIsModalOpen(false);
      setEditTestType({ index: null, value: "" });
      setInitialEditValue("");
      setNewTestType("");
      setIsEditChanged(false);
    } catch (err) {
      swalError("Failed to update test types.");
    } finally {
      setIsUpdating(false);
      setIsCreating(false);
      setIsDeleting(false);
    }
  };

  // Add (supports comma separated)
  const handleCreateTestType = async (e) => {
    e.preventDefault();
    if (!newTestType.trim()) {
      swalError("Please provide the test type value.");
      return;
    }
    setIsCreating(true);
    const newTypes = newTestType
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0 && !testTypes.includes(v));
    if (newTypes.length === 0) {
      swalError("No valid or unique test types to add.");
      setIsCreating(false);
      return;
    }
    const updatedTestTypes = [...testTypes, ...newTypes];
    await updateTestTypesOnServer(
      updatedTestTypes,
      newTypes.length === 1
        ? "Test type created successfully."
        : "Test types created successfully.",
    );
  };

  // Edit
  const handleEditTestType = async (e) => {
    e.preventDefault();
    if (!editTestType.value.trim()) {
      swalError("Please provide the test type value.");
      return;
    }
    setIsUpdating(true);
    const updatedTestTypes = testTypes.map((tt, i) =>
      i === editTestType.index ? editTestType.value.trim() : tt,
    );
    await updateTestTypesOnServer(
      updatedTestTypes,
      "Test type updated successfully.",
    );
  };

  // Delete
  const handleDelete = async (index) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel",
    );
    if (result.isConfirmed) {
      setIsDeleting(true);
      const updatedTestTypes = testTypes.filter((_, i) => i !== index);
      await updateTestTypesOnServer(
        updatedTestTypes,
        "Test type deleted successfully.",
      );
    }
  };

  // Table columns
  const columns = [
    { key: "value", label: "Test Type", sortable: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  // Table data
  const enhancedTestTypes = testTypes.map((tt, i) => ({
    value: tt,
    edit: (
      <button
        className="edit-btn"
        onClick={() => {
          setEditTestType({ index: i, value: tt });
          setInitialEditValue(tt);
          setIsEditModalOpen(true);
          setIsEditChanged(false);
        }}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDelete(i)}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-trash-can"></i>
      </button>
    ),
  }));

  return (
    <>
      <div className="manage-container">
        <div className="header">
          <button className="create-btn" onClick={() => setIsModalOpen(true)}>
            Add Test Type +
          </button>
        </div>
      </div>
      <div className="manage-container">
        {loading && <Loader fontSize="2rem" />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table
            data={enhancedTestTypes}
            columns={columns}
            caption="Test Types"
          />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => !isCreating && setIsModalOpen(false)}
      >
        <h2>Add New Test Type</h2>
        <form onSubmit={handleCreateTestType} className="department-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Test Type
            </label>
            <input
              type="text"
              name="testType"
              value={newTestType}
              placeholder="e.g. MID, FINAL, UNIT TEST"
              onChange={(e) => setNewTestType(e.target.value)}
              required
              disabled={isCreating}
            />
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Test Type
            </button>
          )}
        </form>
      </Modal>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isUpdating && setIsEditModalOpen(false)}
      >
        <h2>Edit Test Type</h2>
        <form onSubmit={handleEditTestType} className="department-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Test Type
            </label>
            <input
              type="text"
              name="testType"
              value={editTestType.value}
              placeholder="e.g. MID, FINAL"
              onChange={(e) => {
                setEditTestType({ ...editTestType, value: e.target.value });
                setIsEditChanged(e.target.value !== initialEditValue);
              }}
              required
              disabled={isUpdating}
            />
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No changes detected.");
                }
              }}
            >
              Update Test Type
            </button>
          )}
        </form>
      </Modal>
    </>
  );
});

export default ManageTestTypes;
